import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect';
import { useRuleMetadata } from '../metadata/RuleMetadataProvider';
import { useRuleApi } from '../useRuleApi';
import { hasErrors, RuleEditorMode, useRuleEditor } from './useRuleEditor';
import RuleTextEditor from './text/RuleTextEditor';
import RuleBuilder from './builder/RuleBuilder';
import DiagnosticsPanel from './DiagnosticsPanel';
import DryRunPanel from './DryRunPanel';

interface ComponentProps {
  /** Undefined means the logged-in user's own company; the admin section passes the row it opened. */
  companyId?: string;
  /** Undefined for a rule that does not exist yet. */
  ruleId?: string;
  readOnly?: boolean;
  onClose: () => void;
  /** Called with the stored rule's id after a successful save. */
  onSaved: (ruleId: string) => void;
}

/**
 * The example a new rule opens on. The DSL itself is never translated — the keywords are the
 * grammar — but the two quoted strings are prose the author reads and then rewrites, so they are.
 */
const starterRule = (tr: (key: string, defaultText?: string) => string): string => `rule "${tr('AiDispatcher.starterRuleName', 'My first rule')}" {
  enabled
  priority 100

  on expedition.created

  when expedition.status == NEW

  then
    log_message(message: "${tr('AiDispatcher.starterRuleMessage', 'The dispatcher saw a new expedition')}")
}
`;

/**
 * The rule editor: a visual builder and a text pane over the same AST, with a dry run beside them.
 *
 * A new rule starts from a small example; an existing one is fetched and opened in whichever pane
 * it was last edited in, so a hand-written rule does not get silently normalised just by being
 * looked at.
 */
const RuleEditor: React.FC<ComponentProps> = ({ companyId, ruleId, readOnly, onClose, onSaved }) => {
  const { tr } = useTranslationLgs();
  const { metadata, loading, failed, reload } = useRuleMetadata();
  const { getRule } = useRuleApi(companyId);

  // Built once: the editor owns the text from here on, so a re-render must not reset it.
  const [starter] = useState(() => starterRule(tr));

  const { state, save, loadRule, setSourceText, setAst, setHeader, enterBuilder, enterText, applyFormatting, builderWillReplaceText, textParses } = useRuleEditor(
    starter,
    undefined,
    companyId,
  );
  const [pendingBuilderSwitch, setPendingBuilderSwitch] = useState(false);
  const [loadingRule, setLoadingRule] = useState(Boolean(ruleId));

  useEffect(() => {
    if (!ruleId) return;
    setLoadingRule(true);
    getRule(ruleId, rule => {
      loadRule(rule);
      setLoadingRule(false);
    });
  }, [ruleId, getRule, loadRule]);

  const requestMode = (mode: RuleEditorMode) => {
    if (mode === state.editorMode) return;
    if (mode === 'TEXT') {
      enterText();
      return;
    }
    // Opening the builder regenerates the source. Ask first whenever that would throw something
    // away — hand-written formatting and comments, or text that does not parse at all.
    if (builderWillReplaceText) {
      setPendingBuilderSwitch(true);
      return;
    }
    enterBuilder();
  };

  if (loading || loadingRule) return <div className="p-4 text-content-secondary">{tr('AiDispatcher.loadingMetadata', 'Loading…')}</div>;

  if (failed || !metadata) {
    return (
      <div className="flex items-center gap-3 p-4">
        <span className="text-content-secondary">{tr('AiDispatcher.metadataFailed', 'The rule vocabulary could not be loaded, so the editor cannot be shown.')}</span>
        <RingilButton mode="text-only" label={tr('AiDispatcher.retry', 'Try again')} onClick={reload} />
      </div>
    );
  }

  const errors = hasErrors(state.diagnostics);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <RingilButton mode="text-only" label={tr('AiDispatcher.backToList', '← All rules')} onClick={onClose} />
          <InputSelect
            model={[
              { code: 'BUILDER', label: tr('AiDispatcher.modeBuilder', 'Builder') },
              { code: 'TEXT', label: tr('AiDispatcher.modeText', 'Text') },
            ]}
            selectedItemCode={state.editorMode}
            onSelect={code => requestMode(code as RuleEditorMode)}
          />
        </div>
        <div className="flex items-center gap-2">
          {state.busy && <span className="text-sm text-content-tertiary">{tr('AiDispatcher.checking', 'Checking…')}</span>}
          {state.editorMode === 'TEXT' && (
            <RingilButton
              mode="table"
              label={tr('AiDispatcher.format', 'Format')}
              tooltip={tr('AiDispatcher.formatTooltip', 'Rewrite the rule in its canonical form')}
              onClick={applyFormatting}
              disabled={readOnly || !state.formatted}
            />
          )}
          <RingilButton
            mode="filled-rectangle"
            label={tr('AiDispatcher.save', 'Save')}
            onClick={() => save(ruleId, rule => onSaved(rule?.id ?? ruleId))}
            loading={state.busy}
            // Errors block saving; warnings do not.
            disabled={readOnly || errors}
          />
        </div>
      </div>

      {state.editorMode === 'TEXT' ? (
        <RuleTextEditor
          value={state.sourceText}
          onChange={setSourceText}
          diagnostics={state.diagnostics}
          metadata={metadata}
          readOnly={readOnly}
          placeholder={tr('AiDispatcher.editorPlaceholder', 'Write a rule…')}
          companyId={companyId}
        />
      ) : state.ast ? (
        <RuleBuilder ast={state.ast} header={state.header} onAstChange={setAst} onHeaderChange={setHeader} companyId={companyId} readOnly={readOnly} />
      ) : (
        <div className="p-4 text-content-secondary">{tr('AiDispatcher.preparingBuilder', 'Preparing the builder…')}</div>
      )}

      {state.editorMode === 'BUILDER' && (
        <section className="flex flex-col gap-2">
          <h4 className="m-0 text-sm font-medium uppercase text-content-secondary">{tr('AiDispatcher.generatedSource', 'Generated rule')}</h4>
          <pre className="overflow-x-auto rounded-md border border-solid border-base-tertiary bg-base-secondary p-3 text-xs">{state.sourceText}</pre>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h4 className="m-0 text-sm font-medium uppercase text-content-secondary">{tr('AiDispatcher.problems', 'Problems')}</h4>
        <DiagnosticsPanel diagnostics={state.diagnostics} />
      </section>

      <section className="flex flex-col gap-2 rounded-md border border-solid border-base-tertiary p-4">
        <h3 className="m-0 text-base font-medium">{tr('AiDispatcher.dryRun', 'Test against a real expedition')}</h3>
        <p className="m-0 text-sm text-content-secondary">
          {tr('AiDispatcher.dryRunHint', 'Evaluates the rule as written and shows what it would do. Nothing is changed.')}
        </p>
        <DryRunPanel sourceText={state.sourceText} ast={state.ast} useAst={state.editorMode === 'BUILDER'} companyId={companyId} disabled={errors} />
      </section>

      <Dialog
        visible={pendingBuilderSwitch}
        onHide={() => setPendingBuilderSwitch(false)}
        header={tr('AiDispatcher.switchToBuilderTitle', 'Switch to the builder?')}
        footer={
          <div className="flex justify-end gap-2">
            <RingilButton mode="text-only" label={tr('AiDispatcher.cancel', 'Cancel')} onClick={() => setPendingBuilderSwitch(false)} />
            <RingilButton
              mode="filled-rectangle"
              label={tr('AiDispatcher.switchToBuilderConfirm', 'Switch and rewrite')}
              onClick={() => {
                setPendingBuilderSwitch(false);
                enterBuilder();
              }}
            />
          </div>
        }
      >
        <p className="m-0 max-w-lg">
          {textParses
            ? tr(
                'AiDispatcher.switchToBuilderWarning',
                'Editing this rule in the builder rewrites its text: comments are removed and the formatting is normalised. The rule itself does not change.',
              )
            : tr(
                'AiDispatcher.switchToBuilderWarningInvalid',
                'This rule does not parse, so the builder cannot show what you typed. It opens the last version that did parse — or an empty rule — and rewrites the text from it. Your current text will be lost.',
              )}
        </p>
      </Dialog>
    </div>
  );
};

export default RuleEditor;
